import { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface CaseDocument {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  description: string | null;
  document_category: string | null;
  uploaded_at: string;
}

interface DocumentUploadProps {
  clientId: string;
  userId: string;
  documents: CaseDocument[];
  onDocumentAdded: (doc: CaseDocument) => void;
}

interface PreviewState {
  doc: CaseDocument;
  url: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
const ALLOWED_EXT_LABEL = 'PDF, JPG, PNG, DOCX, TXT (max 10 MB)';

const DOCUMENT_CATEGORIES = [
  { value: '', label: 'Select category...', color: '', icon: '' },
  { value: 'form_7501', label: 'Form 7501 (Entry Summary)', color: 'bg-[#FDB913]/15 text-[#FDB913] border-[#FDB913]/30', icon: 'ri-file-list-3-line' },
  { value: 'form_4333a', label: 'CBP Form 4333A', color: 'bg-teal-500/15 text-teal-400 border-teal-500/30', icon: 'ri-government-line' },
  { value: 'form_5106', label: 'CBP Form 5106', color: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30', icon: 'ri-id-card-line' },
  { value: 'internal_reconciliation', label: 'Internal Reconciliations', color: 'bg-pink-500/15 text-pink-400 border-pink-500/30', icon: 'ri-calculator-line' },
  { value: 'other', label: 'Other', color: 'bg-white/10 text-white/60 border-white/20', icon: 'ri-file-line' },
];

function getCategoryInfo(value: string | null) {
  return DOCUMENT_CATEGORIES.find(c => c.value === value) ?? DOCUMENT_CATEGORIES[0];
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(type: string): string {
  if (type === 'application/pdf') return 'ri-file-pdf-line text-red-400';
  if (type.startsWith('image/')) return 'ri-image-line text-green-400';
  if (type.includes('word')) return 'ri-file-word-line text-[#FDB913]';
  return 'ri-file-text-line text-white/60';
}

function isPreviewable(type: string): boolean {
  return type === 'application/pdf' || type.startsWith('image/');
}

export default function DocumentUpload({ clientId, userId, documents, onDocumentAdded }: DocumentUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [description, setDescription] = useState('');
  const [documentCategory, setDocumentCategory] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const handleFileSelect = (file: File) => {
    setUploadError('');
    setUploadSuccess('');
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('File type not supported. Please upload a PDF, JPG, PNG, DOCX, or TXT file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size exceeds 10 MB limit.');
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      const timestamp = Date.now();
      const safeFileName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${userId}/${timestamp}_${safeFileName}`;
      const { error: storageError } = await supabase.storage
        .from('case-documents')
        .upload(filePath, selectedFile, { cacheControl: '3600', upsert: false });
      if (storageError) throw storageError;
      const { data: dbData, error: dbError } = await supabase
        .from('case_documents')
        .insert({
          client_id: clientId,
          file_name: selectedFile.name,
          file_path: filePath,
          file_size: selectedFile.size,
          file_type: selectedFile.type,
          description: description.trim() || null,
          document_category: documentCategory || null,
        })
        .select()
        .single();
      if (dbError) throw dbError;
      onDocumentAdded(dbData as CaseDocument);
      setSelectedFile(null);
      setDescription('');
      setDocumentCategory('');
      setUploadSuccess(`"${selectedFile.name}" uploaded successfully.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: CaseDocument) => {
    setDownloadingId(doc.id);
    try {
      const { data, error } = await supabase.storage.from('case-documents').download(doc.file_path);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = async (doc: CaseDocument) => {
    setPreviewLoading(doc.id);
    try {
      const { data, error } = await supabase.storage
        .from('case-documents')
        .createSignedUrl(doc.file_path, 3600);
      if (error) throw error;
      if (data?.signedUrl) {
        setPreview({ doc, url: data.signedUrl });
      }
    } catch (err) {
      console.error('Preview failed:', err);
    } finally {
      setPreviewLoading(null);
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
      {/* Document Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 bg-[#0d1520] border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                <i className={`${getFileIcon(preview.doc.file_type)} text-base`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm truncate">{preview.doc.file_name}</p>
                <p className="text-white/40 text-xs">{formatFileSize(preview.doc.file_size)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => handleDownload(preview.doc)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line"></i>
                Download
              </button>
              <button
                onClick={() => setPreview(null)}
                className="w-9 h-9 flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 rounded-lg transition-all cursor-pointer"
              >
                <i className="ri-close-line text-red-400 text-lg"></i>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden flex items-center justify-center p-4">
            {preview.doc.file_type === 'application/pdf' ? (
              <iframe
                src={preview.url}
                title={preview.doc.file_name}
                className="w-full h-full rounded-lg border border-white/10"
                style={{ minHeight: '70vh' }}
              />
            ) : (
              <div className="max-w-full max-h-full flex items-center justify-center">
                <img
                  src={preview.url}
                  alt={preview.doc.file_name}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <h3 className="text-2xl font-black text-white mb-2">Case Documents</h3>
      <p className="text-white/50 text-sm mb-6">
        Upload and categorize your case files — Entry Summary, CBP forms, and supporting documents.
      </p>

      {/* Document category legend */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {DOCUMENT_CATEGORIES.slice(1).map(cat => (
          <span key={cat.value} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bold ${cat.color}`}>
            <i className={`${cat.icon} text-xs`}></i>
            {cat.label}
          </span>
        ))}
      </div>

      {/* Upload area */}
      <div
        className={`border-2 border-dashed rounded-xl p-6 text-center mb-4 transition-all ${
          dragOver ? 'border-[#FDB913] bg-[#FDB913]/10' : selectedFile ? 'border-green-500/60 bg-green-500/5' : 'border-white/20 hover:border-white/40 bg-white/5'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {selectedFile ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                <i className={`${getFileIcon(selectedFile.type)} text-xl`}></i>
              </div>
              <div className="min-w-0 text-left">
                <p className="text-white text-sm font-bold truncate">{selectedFile.name}</p>
                <p className="text-white/50 text-xs">{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            <button
              onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
              className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-red-500/30 rounded-full flex-shrink-0 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-white/60 text-sm"></i>
            </button>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-3">
              <i className="ri-upload-cloud-2-line text-white/50 text-2xl"></i>
            </div>
            <p className="text-white font-bold text-sm mb-1">Drop file here or click to browse</p>
            <p className="text-white/40 text-xs">{ALLOWED_EXT_LABEL}</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-4 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-bold px-5 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-folder-open-line"></i>
              Browse Files
            </button>
          </>
        )}
        <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.txt" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
      </div>

      {selectedFile && (
        <div className="mb-4 space-y-3">
          {/* Category selector */}
          <div>
            <label className="block text-white/70 text-xs font-bold mb-1.5 uppercase tracking-wider">
              Document Category <span className="text-red-400">*</span>
              <span className="text-white/30 normal-case font-normal ml-1">— helps the legal team sort your files</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {DOCUMENT_CATEGORIES.slice(1).map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setDocumentCategory(cat.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer text-left ${
                    documentCategory === cat.value
                      ? `${cat.color} border-current scale-[1.02]`
                      : 'border-white/10 bg-white/5 text-white/50 hover:border-white/20 hover:text-white/70'
                  }`}
                >
                  <i className={`${cat.icon} flex-shrink-0`}></i>
                  <span className="leading-tight">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
          {/* Description */}
          <div>
            <label className="block text-white/70 text-xs font-bold mb-1 uppercase tracking-wider">
              Description <span className="text-white/40 normal-case font-normal">(optional)</span>
            </label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} placeholder="e.g. Entry summary for shipment #12345..." className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm" />
          </div>
        </div>
      )}

      {uploadError && (
        <div className="bg-red-500/20 border border-red-500/50 text-white px-4 py-3 rounded-lg text-sm mb-4">
          <i className="ri-error-warning-line mr-2"></i>{uploadError}
        </div>
      )}
      {uploadSuccess && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-lg text-sm mb-4">
          <i className="ri-checkbox-circle-line mr-2"></i>{uploadSuccess}
        </div>
      )}

      {selectedFile && (
        <button onClick={handleUpload} disabled={uploading} className="w-full bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-base py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mb-6">
          {uploading ? <><i className="ri-loader-4-line animate-spin"></i> Uploading...</> : <><i className="ri-upload-2-line"></i> Upload Document</>}
        </button>
      )}

      {/* Documents list */}
      {documents.length > 0 ? (
        <div>
          <h4 className="text-white/70 text-xs font-black uppercase tracking-widest mb-3">
            Uploaded Files ({documents.length})
          </h4>
          <div className="space-y-2">
            {documents.map((doc) => {
              const catInfo = getCategoryInfo(doc.document_category);
              return (
                <div key={doc.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3 hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className={`${getFileIcon(doc.file_type)} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white text-sm font-bold truncate">{doc.file_name}</p>
                      {catInfo.value && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bold whitespace-nowrap ${catInfo.color}`}>
                          <i className={`${catInfo.icon} text-xs`}></i>
                          {catInfo.label}
                        </span>
                      )}
                    </div>
                    <p className="text-white/40 text-xs mt-0.5">
                      {formatFileSize(doc.file_size)} &bull;{' '}
                      {new Date(doc.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    {doc.description && (
                      <p className="text-white/50 text-xs truncate mt-0.5">{doc.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isPreviewable(doc.file_type) && (
                      <button
                        onClick={() => handlePreview(doc)}
                        disabled={previewLoading === doc.id}
                        className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-[#FDB913]/20 border border-white/20 hover:border-[#FDB913]/40 rounded-lg transition-all cursor-pointer disabled:opacity-50"
                        title="Preview"
                      >
                        {previewLoading === doc.id
                          ? <i className="ri-loader-4-line animate-spin text-white/60 text-base"></i>
                          : <i className="ri-eye-line text-white/60 hover:text-[#FDB913] text-base"></i>
                        }
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc.id}
                      className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-[#FDB913]/20 border border-white/20 hover:border-[#FDB913]/40 rounded-lg transition-all cursor-pointer flex-shrink-0 disabled:opacity-50"
                      title="Download"
                    >
                      {downloadingId === doc.id
                        ? <i className="ri-loader-4-line animate-spin text-white/60 text-base"></i>
                        : <i className="ri-download-2-line text-white/60 hover:text-[#FDB913] text-base"></i>
                      }
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <i className="ri-folder-3-line text-white/20 text-5xl mb-3"></i>
          <p className="text-white/40 text-sm">No documents uploaded yet</p>
          <p className="text-white/30 text-xs mt-1">Your uploaded files will appear here</p>
        </div>
      )}
    </div>
  );
}
